import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { apiClient } from "@/lib/api";
import { BookingWindow, BookingWindowStatus } from "@/types/booking-window";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Save, Trash2, Pencil } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Create a function to generate the schema with conditional validation
const createBookingWindowSchema = (isEditing: boolean = false, originalData?: any) => {
  return z.object({
    name: z.string().min(3, "Name must be at least 3 characters").max(100, "Name cannot exceed 100 characters"),
    slug: z.string().min(3, "Slug must be at least 3 characters").max(50, "Slug cannot exceed 50 characters").regex(/^[a-z0-9-]+$/, "Slug must contain only lowercase letters, numbers, and hyphens"),
    status: z.nativeEnum(BookingWindowStatus),
    submission_start_date: z.string().min(1, "Submission start date is required"),
    submission_end_date: z.string().min(1, "Submission end date is required"),
    packaging_start_date: z.string().min(1, "Packaging start date is required"),
    packaging_end_date: z.string().min(1, "Packaging end date is required"),
    presentation_date: z.string().min(1, "Presentation date is required"),
  }).refine((data) => {
    // Only validate future dates if:
    // 1. Creating a new booking window (not editing), OR
    // 2. Editing and the submission_start_date has actually changed
    
    // Helper function to normalize datetime strings for comparison
    const normalizeDateTimeString = (dateStr: string) => {
      if (!dateStr) return "";
      // Remove any trailing 'Z' and normalize to consistent format
      return dateStr.replace(/Z$/, "").trim();
    };
    
    // Check if we're creating a new window
    if (!isEditing) {
      console.log("🔍 VALIDATION: Creating new window - validating future dates");
      if (data.submission_start_date) {
        const now = new Date();
        const submissionStart = new Date(data.submission_start_date);
        const isValid = submissionStart >= now;
        console.log("🔍 VALIDATION: Future date check result:", { isValid, now: now.toISOString(), submissionStart: submissionStart.toISOString() });
        return isValid;
      }
      return true;
    }
    
    // For editing: only validate if dates actually changed
    if (!originalData) {
      console.log("🔍 VALIDATION: No original data available - skipping validation");
      return true;
    }
    
    // Normalize both dates for accurate comparison
    const currentNormalized = normalizeDateTimeString(data.submission_start_date);
    const originalNormalized = normalizeDateTimeString(originalData.submission_start_date);
    const dateActuallyChanged = currentNormalized !== originalNormalized;
    
    console.log("🔍 VALIDATION DEBUG:", {
      isEditing,
      currentDate: data.submission_start_date,
      originalDate: originalData.submission_start_date,
      currentNormalized,
      originalNormalized,
      dateActuallyChanged
    });
    
    if (dateActuallyChanged) {
      console.log("🔍 VALIDATION: Date changed - validating future date");
      if (data.submission_start_date) {
        const now = new Date();
        const submissionStart = new Date(data.submission_start_date);
        const isValid = submissionStart >= now;
        console.log("🔍 VALIDATION: Future date check result:", { isValid, now: now.toISOString(), submissionStart: submissionStart.toISOString() });
        return isValid;
      }
    } else {
      console.log("🔍 VALIDATION: Date unchanged - skipping future date validation");
    }
    
    return true;
  }, {
    message: "Submission start date cannot be in the past",
    path: ["submission_start_date"],
  }).refine((data) => {
    if (data.submission_start_date && data.submission_end_date) {
      return new Date(data.submission_end_date) > new Date(data.submission_start_date);
    }
    return true;
  }, {
    message: "Submission end date must be after start date",
    path: ["submission_end_date"],
  }).refine((data) => {
    if (data.packaging_start_date && data.packaging_end_date) {
      return new Date(data.packaging_end_date) > new Date(data.packaging_start_date);
    }
    return true;
  }, {
    message: "Packaging end date must be after start date",
    path: ["packaging_end_date"],
  }).refine((data) => {
    if (data.submission_end_date && data.packaging_start_date) {
      return new Date(data.packaging_start_date) >= new Date(data.submission_end_date);
    }
    return true;
  }, {
    message: "Packaging must start on or after submission ends",
    path: ["packaging_start_date"],
  }).refine((data) => {
    if (data.packaging_end_date && data.presentation_date) {
      return new Date(data.presentation_date) >= new Date(data.packaging_end_date);
    }
    return true;
  }, {
    message: "Presentation must be on or after packaging ends",
    path: ["presentation_date"],
  });
};

// Default schema for backward compatibility
const bookingWindowSchema = createBookingWindowSchema();

type BookingWindowFormValues = z.infer<typeof bookingWindowSchema>;

export default function BookingWindowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isEditMode = location.pathname.endsWith("/edit");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bookingWindow, setBookingWindow] = useState<BookingWindow | null>(null);
  const [originalFormData, setOriginalFormData] = useState<BookingWindowFormValues | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Handle browser back button navigation for edit mode
  useEffect(() => {
    if (isEditMode) {
      // Replace the current history entry so back button goes to booking windows list
      window.history.replaceState(
        { fromBookingWindowsList: true },
        '',
        window.location.pathname
      );
      
      const handlePopState = (event: PopStateEvent) => {
        // Always redirect to booking windows list when back button is pressed from edit mode
        navigate("/admin/booking-windows", { replace: true });
      };

      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isEditMode, navigate]);

  // Get current date in the user's local timezone for datetime-local input
  // datetime-local inputs work in the user's local timezone, so we use local time
  const getCurrentDateTime = () => {
    const now = new Date();
    // Get local date/time components (this automatically handles user's timezone)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const minDateTime = getCurrentDateTime();

  // Create conditional schema based on whether we're editing and have original data
  const currentSchema = useMemo(() => {
    const isEditing = !!id && !!originalFormData;
    console.log("🔍 SCHEMA CREATION:", {
      id,
      hasOriginalFormData: !!originalFormData,
      isEditing,
      originalFormData: originalFormData ? {
        submission_start_date: originalFormData.submission_start_date,
        name: originalFormData.name,
        status: originalFormData.status
      } : null
    });
    return createBookingWindowSchema(isEditing, originalFormData);
  }, [id, originalFormData]);

  const form = useForm<BookingWindowFormValues>({
    resolver: zodResolver(currentSchema),
    defaultValues: {
      name: "",
      slug: "",
      status: BookingWindowStatus.DRAFT,
      submission_start_date: "",
      submission_end_date: "",
      packaging_start_date: "",
      packaging_end_date: "",
      presentation_date: "",
    },
  });

  // Update form resolver when schema changes
  useEffect(() => {
    form.clearErrors(); // Clear any existing validation errors
  }, [currentSchema, form]);

  useEffect(() => {
    if (id) {
      fetchBookingWindow(id);
    }
  }, [id, location.pathname]);

  // Helper function to format UTC datetime for datetime-local input
  // Converts UTC from backend to user's local time for form editing
  const formatDateTimeLocal = (utcDateString: string) => {
    if (!utcDateString) return "";
    
    // CRITICAL FIX: Backend sends dates without "Z" suffix, so JavaScript interprets them as local time
    // We need to explicitly treat them as UTC by adding "Z" suffix
    const utcDateStringWithZ = utcDateString.endsWith('Z') ? utcDateString : utcDateString + 'Z';
    
    // Parse the UTC date string from backend
    const utcDate = new Date(utcDateStringWithZ);
    
    // Check if the date is valid
    if (isNaN(utcDate.getTime())) {
      console.error("Invalid UTC date string:", utcDateStringWithZ);
      return "";
    }
    
    // Convert UTC to user's local time for datetime-local input
    // The datetime-local input expects the time in the user's timezone
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');
    const hours = String(utcDate.getHours()).padStart(2, '0');
    const minutes = String(utcDate.getMinutes()).padStart(2, '0');
    
    const localFormatted = `${year}-${month}-${day}T${hours}:${minutes}`;
    
    return localFormatted;
  };

  // Helper function to format datetime for display (not form input)
  // Converts UTC datetime from backend to user's local timezone
  const formatDateTimeDisplay = (dateString: string) => {
    if (!dateString) return "";
    
    // CRITICAL FIX: Backend sends dates without "Z" suffix, so JavaScript interprets them as local time
    // We need to explicitly treat them as UTC by adding "Z" suffix
    const utcDateStringWithZ = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    
    // Parse the UTC date string from backend
    const utcDate = new Date(utcDateStringWithZ);
    
    if (isNaN(utcDate.getTime())) {
      console.error("Invalid UTC date string:", utcDateStringWithZ);
      return "";
    }
    
    // Convert UTC to user's local timezone and format
    // toLocaleString automatically converts UTC to local timezone
    const localFormatted = utcDate.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    return localFormatted;
  };

  const fetchBookingWindow = async (windowId: string) => {
    try {
      setIsLoading(true);
      const data = await apiClient.getAdminBookingWindow(windowId);
      setBookingWindow(data);
      
      // Prepare form data with timezone-aware formatting
      const formData = {
        name: data.name,
        slug: data.slug,
        status: data.status,
        submission_start_date: formatDateTimeLocal(data.submission_start_date),
        submission_end_date: formatDateTimeLocal(data.submission_end_date),
        packaging_start_date: formatDateTimeLocal(data.packaging_start_date),
        packaging_end_date: formatDateTimeLocal(data.packaging_end_date),
        presentation_date: formatDateTimeLocal(data.presentation_date),
      };
      
      // Store original form data for conditional validation
      console.log("🔍 SETTING ORIGINAL FORM DATA:", formData);
      setOriginalFormData(formData);
      
      // Reset form with fetched data
      form.reset(formData);
      console.log("🔍 FORM RESET COMPLETE with data:", formData);
    } catch (error) {
      console.error("Error fetching booking window:", error);
      toast({
        title: "Error",
        description: "Failed to load booking window details",
        variant: "destructive",
      });
      navigate("/admin/booking-windows");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to convert local datetime to UTC for backend storage
  const convertToUTC = (localDateTimeString: string) => {
    if (!localDateTimeString) return "";
    
    // CRITICAL: datetime-local input gives us a string like "2026-03-23T04:05"
    // This represents the LOCAL time in the user's timezone
    // We need to interpret this as local time and convert to UTC
    
    // Parse the datetime string components
    const [datePart, timePart] = localDateTimeString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    // Create a date object using the local timezone interpretation
    // This ensures the date is created in the user's current timezone
    const localDate = new Date(year, month - 1, day, hours, minutes);
    
    // Convert to UTC ISO string
    const utcString = localDate.toISOString();
    
    return utcString;
  };

  async function onSubmit(data: BookingWindowFormValues) {
    if (!id) return;
    
    setIsSaving(true);
    try {
      console.log("🔍 DEBUG - EDIT FLOW - Original form data:", data);
      
      // Convert all datetime fields to UTC before sending to backend
      const utcData = {
        ...data,
        submission_start_date: convertToUTC(data.submission_start_date),
        submission_end_date: convertToUTC(data.submission_end_date),
        packaging_start_date: convertToUTC(data.packaging_start_date),
        packaging_end_date: convertToUTC(data.packaging_end_date),
        presentation_date: convertToUTC(data.presentation_date),
      };
      
      console.log("🔍 DEBUG - EDIT FLOW - UTC converted data:", utcData);
      
      await apiClient.updateBookingWindow(id, utcData);
      
      toast({
        title: "Success",
        description: "Booking window updated successfully",
      });
      
      // IMPORTANT: Don't refetch data immediately after save to prevent timezone conversion
      // The form already has the correct values that the user entered
      // Only update the bookingWindow state for display purposes
      
      if (bookingWindow) {
        setBookingWindow({
          ...bookingWindow,
          name: data.name,
          slug: data.slug,
          status: data.status,
          // Keep the original date strings to prevent timezone conversion issues
          updated_at: new Date().toISOString() // Update timestamp
        });
      }
      
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update booking window",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  }

  const handleDelete = async () => {
    if (!id) return;
    
    setIsDeleting(true);
    try {
      await apiClient.deleteBookingWindow(id);
      
      toast({
        title: "Success",
        description: "Booking window and associated data deleted successfully",
      });
      
      navigate("/admin/booking-windows");
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete booking window",
        variant: "destructive",
      });
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!bookingWindow) {
    return null;
  }

  return (
    <div className="container mx-auto py-10 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/booking-windows">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEditMode ? "Edit Booking Window" : "View Booking Window"}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode ? "Manage details for" : "Viewing details for"} {bookingWindow.name}
            </p>
          </div>
        </div>
        
        {isEditMode ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Window
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Booking Window</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{bookingWindow.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive hover:bg-destructive/90"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <Button onClick={() => navigate(`/admin/booking-windows/${id}/edit`, { replace: true })}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit Window
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Window Details</CardTitle>
              <CardDescription>
                Configure the timeline and status for this booking window.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">
                          Window Name {isEditMode && <span className="text-red-500">*</span>}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!isEditMode} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="slug"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">
                          Slug {isEditMode && <span className="text-red-500">*</span>}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!isEditMode} />
                        </FormControl>
                        <FormDescription>URL-friendly identifier (lowercase, hyphens)</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="font-medium text-sm text-muted-foreground">Submission Period</h3>
                      <FormField
                        control={form.control}
                        name="submission_start_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground">
                              Start Date {isEditMode && <span className="text-red-500">*</span>}
                            </FormLabel>
                            <FormControl>
                              {isEditMode ? (
                                <Input
                                  type="datetime-local"
                                  {...field}
                                  min={
                                    // Only enforce min date if:
                                    // 1. Creating new window, OR
                                    // 2. The date has actually changed from original
                                    !originalFormData ||
                                    (originalFormData && field.value !== originalFormData.submission_start_date)
                                      ? minDateTime
                                      : undefined
                                  }
                                  max="2030-12-31T23:59"
                                />
                              ) : (
                                <div className="px-3 py-2 bg-muted rounded-md text-sm">
                                  {bookingWindow?.submission_start_date ? formatDateTimeDisplay(bookingWindow.submission_start_date) : "Not set"}
                                </div>
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="submission_end_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground">
                              End Date {isEditMode && <span className="text-red-500">*</span>}
                            </FormLabel>
                            <FormControl>
                              {isEditMode ? (
                                <Input
                                  type="datetime-local"
                                  {...field}
                                  min={
                                    // Only enforce min date if:
                                    // 1. Creating new window, OR
                                    // 2. The date has actually changed from original
                                    !originalFormData ||
                                    (originalFormData && field.value !== originalFormData.submission_end_date)
                                      ? minDateTime
                                      : undefined
                                  }
                                  max="2030-12-31T23:59"
                                />
                              ) : (
                                <div className="px-3 py-2 bg-muted rounded-md text-sm">
                                  {bookingWindow?.submission_end_date ? formatDateTimeDisplay(bookingWindow.submission_end_date) : "Not set"}
                                </div>
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-medium text-sm text-muted-foreground">Packaging Period</h3>
                      <FormField
                        control={form.control}
                        name="packaging_start_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground">
                              Start Date {isEditMode && <span className="text-red-500">*</span>}
                            </FormLabel>
                            <FormControl>
                              {isEditMode ? (
                                <Input
                                  type="datetime-local"
                                  {...field}
                                  min={
                                    // Only enforce min date if:
                                    // 1. Creating new window, OR
                                    // 2. The date has actually changed from original
                                    !originalFormData ||
                                    (originalFormData && field.value !== originalFormData.packaging_start_date)
                                      ? minDateTime
                                      : undefined
                                  }
                                  max="2030-12-31T23:59"
                                />
                              ) : (
                                <div className="px-3 py-2 bg-muted rounded-md text-sm">
                                  {bookingWindow?.packaging_start_date ? formatDateTimeDisplay(bookingWindow.packaging_start_date) : "Not set"}
                                </div>
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="packaging_end_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-foreground">
                              End Date {isEditMode && <span className="text-red-500">*</span>}
                            </FormLabel>
                            <FormControl>
                              {isEditMode ? (
                                <Input
                                  type="datetime-local"
                                  {...field}
                                  min={
                                    // Only enforce min date if:
                                    // 1. Creating new window, OR
                                    // 2. The date has actually changed from original
                                    !originalFormData ||
                                    (originalFormData && field.value !== originalFormData.packaging_end_date)
                                      ? minDateTime
                                      : undefined
                                  }
                                  max="2030-12-31T23:59"
                                />
                              ) : (
                                <div className="px-3 py-2 bg-muted rounded-md text-sm">
                                  {bookingWindow?.packaging_end_date ? formatDateTimeDisplay(bookingWindow.packaging_end_date) : "Not set"}
                                </div>
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <FormField
                      control={form.control}
                      name="presentation_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground">
                            Presentation Date {isEditMode && <span className="text-red-500">*</span>}
                          </FormLabel>
                          <FormControl>
                            {isEditMode ? (
                              <Input
                                type="datetime-local"
                                {...field}
                                min={
                                  // Only enforce min date if:
                                  // 1. Creating new window, OR
                                  // 2. The date has actually changed from original
                                  !originalFormData ||
                                  (originalFormData && field.value !== originalFormData.presentation_date)
                                    ? minDateTime
                                    : undefined
                                }
                                max="2030-12-31T23:59"
                              />
                            ) : (
                              <div className="px-3 py-2 bg-muted rounded-md text-sm">
                                {bookingWindow?.presentation_date ? formatDateTimeDisplay(bookingWindow.presentation_date) : "Not set"}
                              </div>
                            )}
                          </FormControl>
                          <FormDescription>Date for presentation to brands</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground">
                            Status {isEditMode && <span className="text-red-500">*</span>}
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value} disabled={!isEditMode}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={BookingWindowStatus.DRAFT}>Draft</SelectItem>
                              <SelectItem value={BookingWindowStatus.OPEN}>Open</SelectItem>
                              <SelectItem value={BookingWindowStatus.PACKAGING}>Packaging</SelectItem>
                              <SelectItem value={BookingWindowStatus.PRESENTATION}>Presentation</SelectItem>
                              <SelectItem value={BookingWindowStatus.CLOSED}>Closed</SelectItem>
                              <SelectItem value={BookingWindowStatus.ARCHIVED}>Archived</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {isEditMode && (
                  <div className="flex justify-end gap-4 pt-4">
                    <Button variant="outline" type="button" onClick={() => navigate("/admin/booking-windows")}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                  )}
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="text-sm font-medium text-muted-foreground">Total Campaigns</div>
                <div className="text-2xl font-bold">{bookingWindow.campaign_count || 0}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Total Budget Submitted</div>
                <div className="text-2xl font-bold">
                  ${(bookingWindow.total_budget_submitted || 0).toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">Eligible Projects</div>
                <div className="text-2xl font-bold">{bookingWindow.eligible_project_count || 0}</div>
              </div>
              
              <div className="pt-4 border-t">
                 <div className="text-sm text-muted-foreground">
                    Created: {format(new Date(bookingWindow.created_at), "PPP")}
                 </div>
                 <div className="text-sm text-muted-foreground">
                    Last Updated: {format(new Date(bookingWindow.updated_at), "PPP")}
                 </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
